/**
 * SkinnrContext.tsx - SkinnrContext - React context wrapper for Skinnr
 * 
 * CHANGE HISTORY:
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import Skinnr from './SkinnrManager.js';

export interface SkinnrProviderProps {
  children: ReactNode;
  namespace?: string;
  options?: Record<string, any>;
  initialTheme?: string;
}

const SkinnrContext = createContext<any>(null);

export const SkinnrProvider = ({ children, namespace = 'app', options = {}, initialTheme = 'light' }: SkinnrProviderProps) => {
  // Skinnr Manager will get Actr itself if needed
  const skinnrRef = useRef(null);
  const [, forceUpdate] = useState({});
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  
  // Initialize Skinnr (it will get Actr from Dependr if available)
  if (!skinnrRef.current) {
    skinnrRef.current = new Skinnr(namespace, options);
  }
  
  const skinnr = skinnrRef.current;
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = skinnr.subscribe((event, ...args) => {
      forceUpdate({});
      
      // Trigger onChange if provided
      if (options.onChange) {
        options.onChange(event, ...args);
      }
    });
    
    return unsubscribe;
  }, [skinnr, options]);
  
  // Skin operations
  const registerSkin = useCallback((id, config) => skinnr.registerSkin(id, config), [skinnr]);
  const updateSkin = useCallback((id, updates) => skinnr.updateSkin(id, updates), [skinnr]);
  const deleteSkin = useCallback((id) => skinnr.deleteSkin(id), [skinnr]);
  const getSkin = useCallback((id) => skinnr.getSkin(id), [skinnr]);
  const getAllSkins = useCallback(() => skinnr.getAllSkins(), [skinnr]);
  const applySkin = useCallback((id, container) => skinnr.applySkin(id, container), [skinnr]);
  const getActiveSkin = useCallback(() => skinnr.getActiveSkin(), [skinnr]);
  
  // Mode operations
  const getCurrentMode = useCallback(() => skinnr.getCurrentMode(), [skinnr]);
  const toggleMode = useCallback(() => skinnr.toggleMode(), [skinnr]);
  const findSkinByMode = useCallback((mode) => skinnr.findSkinByMode(mode), [skinnr]);
  
  // Import/Export
  const exportSkin = useCallback((id) => skinnr.exportSkin(id), [skinnr]);
  const importSkin = useCallback((json, overwrite) => skinnr.importSkin(json, overwrite), [skinnr]);
  const cloneSkin = useCallback((sourceId, newId, newName) => skinnr.cloneSkin(sourceId, newId, newName), [skinnr]);
  
  // Utilities
  const generateCSS = useCallback((skinId, selector) => skinnr.generateCSS(skinId, selector), [skinnr]);
  const validateSkin = useCallback((config) => skinnr.validateSkin(config), [skinnr]);
  const mergeSkins = useCallback((baseId, overlayId, newId, newName) => skinnr.mergeSkins(baseId, overlayId, newId, newName), [skinnr]);
  const batchUpdate = useCallback((updates) => skinnr.batchUpdate(updates), [skinnr]);
  const getStats = useCallback(() => skinnr.getStats(), [skinnr]);
  
  // Control
  const reset = useCallback(() => skinnr.reset(), [skinnr]);
  const clearAll = useCallback(() => skinnr.clearAll(), [skinnr]);
  
  // Theme management
  const setTheme = useCallback((theme) => {
    setCurrentTheme(theme);
    // Update data-theme attribute on document
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, []);
  
  // Set initial theme
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', currentTheme);
    }
  }, []);
  
  const value = useMemo(() => ({
    skinnr,
    registerSkin,
    updateSkin,
    deleteSkin,
    getSkin,
    getAllSkins,
    applySkin,
    getActiveSkin,
    getCurrentMode,
    toggleMode,
    findSkinByMode,
    exportSkin,
    importSkin,
    cloneSkin,
    generateCSS,
    validateSkin,
    mergeSkins,
    batchUpdate,
    getStats,
    reset,
    clearAll,
    currentTheme,
    setTheme
  }), [skinnr, registerSkin, updateSkin, deleteSkin, getSkin, getAllSkins, applySkin, getActiveSkin, getCurrentMode, toggleMode, findSkinByMode, exportSkin, importSkin, cloneSkin, generateCSS, validateSkin, mergeSkins, batchUpdate, getStats, reset, clearAll, currentTheme, setTheme]);
  
  return (
    <SkinnrContext.Provider value={value}>
      {children}
    </SkinnrContext.Provider>
  );
};

export const useSkinnr = () => {
  const context = useContext(SkinnrContext);
  
  if (!context) {
    throw new Error('useSkinnr must be used within SkinnrProvider');
  }
  
  return context;
};

/**
 * Hook to automatically apply a skin for a component
 */
export const useApplySkin = (skinId: unknown, containerRef = null) => {
  const { applySkin, getSkin } = useSkinnr();
  
  useEffect(() => {
    if (skinId) {
      const container = containerRef?.current || document.documentElement;
      applySkin(skinId, container);
    }
  }, [skinId, applySkin, containerRef]);
  
  return getSkin(skinId);
};

/**
 * Hook for dark mode toggle
 */
export const useDarkMode = () => {
  const { getCurrentMode, toggleMode, findSkinByMode } = useSkinnr();
  const [mode, setMode] = useState(getCurrentMode());
  
  useEffect(() => {
    setMode(getCurrentMode());
  }, [getCurrentMode]);
  
  const toggle = useCallback(() => {
    const success = toggleMode();
    if (success) {
      setMode(getCurrentMode());
    }
    return success;
  }, [toggleMode, getCurrentMode]);
  
  return {
    mode,
    isDark: mode === 'dark',
    isLight: mode === 'light',
    toggle,
    darkSkin: findSkinByMode('dark'),
    lightSkin: findSkinByMode('light')
  };
};

export default SkinnrContext;
